/**
 * Markdown — Thin wrapper around react-markdown for report body text.
 *
 * The AI-generated scenario summaries, pivot descriptions, lens summaries,
 * and solution descriptions ship as markdown (leading `# H1`, `## H2`
 * subsections, `**bold**` emphasis, plain paragraphs). They were rendering
 * as literal `**word**` text in cards. This wrapper makes them render
 * properly while behaving sensibly inside the constrained card layout:
 *
 *   - Leading `# Heading` is stripped (the card title above is the heading
 *     for the block; rendering the markdown H1 too would duplicate it).
 *   - Headings of any level render as inline-block bold (no oversized H1
 *     fonts that blow up the card).
 *   - `**bold**` → `<strong>`, `*italic*` → `<em>`, `- list` → bullets.
 *   - Paragraph breaks preserved.
 *
 * Used by EditableText via its `markdown` prop, and standalone wherever
 * a static markdown blob needs the same treatment.
 */
import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const Block = styled.div`
  /* Tighten the default react-markdown spacing so paragraphs sit close
   * inside a card body without losing rhythm. */
  & > p { margin: 0 0 0.5em; }
  & > p:last-child { margin-bottom: 0; }
  & strong { font-weight: 700; }
  & em { font-style: italic; }
  & ul, & ol { margin: 0.25em 0 0.5em 1.25em; padding: 0; }
  & li { margin: 0; }
  /* Headings inside body prose get demoted to inline-style emphasis so
   * the card title above stays the visual heading. Adds a small top
   * margin if not the first thing in the block. */
  & h1, & h2, & h3, & h4, & h5, & h6 {
    display: block;
    font-size: 1em;
    font-weight: 700;
    margin: 0.75em 0 0.25em;
  }
  & h1:first-of-type, & h2:first-of-type, & h3:first-of-type {
    margin-top: 0;
  }
`;

/* Drop a leading `# Heading` line — the AI uses it as a section banner
 * but inside a report card the card title already plays that role. */
function stripLeadingH1(text) {
  if (!text) return '';
  return text.replace(/^\s*#\s+[^\n]*\n+/, '');
}

export default function Markdown({ children }) {
  const source = useMemo(() => stripLeadingH1(String(children || '')), [children]);
  if (!source) return null;
  return (
    <Block>
      <ReactMarkdown remarkPlugins={[remarkGfm]}>
        {source}
      </ReactMarkdown>
    </Block>
  );
}
